import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ToastService } from './toast.service';

export interface ShiprocketAuthResponse {
  token: string;
  expires_at?: number;
}

export interface ServiceabilityResponse {
  data: {
    available_courier_companies: Array<{
      id: number;
      courier_company_id: number;
      courier_name: string;
      rate: number;
      estimated_delivery_days: number;
      cod: number; // 0 or 1
      etd: string;
      pickup_availability: string;
      [key: string]: any; // Allow other properties
    }>;
    recommended_courier_company_id?: number;
    shiprocket_recommended_courier_id?: number;
  };
  status: number;
  [key: string]: any; // Allow other properties
}

export interface CreateShipmentRequest {
  order_id: string;
  order_date: string;
  pickup_location: string;
  billing_customer_name: string;
  billing_last_name: string;
  billing_address: string;
  billing_address_2?: string;
  billing_city: string;
  billing_pincode: string;
  billing_state: string;
  billing_country: string;
  billing_email: string;
  billing_phone: string;
  shipping_is_billing: boolean;
  shipping_customer_name: string;
  shipping_last_name: string;
  shipping_address: string;
  shipping_address_2?: string;
  shipping_city: string;
  shipping_pincode: string;
  shipping_state: string;
  shipping_country: string;
  shipping_email: string;
  shipping_phone: string;
  order_items: Array<{
    name: string;
    sku: string;
    units: number;
    selling_price: number;
  }>;
  payment_method: string;
  sub_total: number;
  length?: number;
  breadth?: number;
  height?: number;
  weight?: number;
}

export interface CreateShipmentResponse {
  order_id: number;
  shipment_id: number;
  status: string;
  status_code: number;
  onboarding_completed_now: number;
  awb_code?: string;
  courier_company_id?: number;
  courier_name?: string;
}

export interface AssignAWBResponse {
  status: number;
  message: string;
  data: {
    awb_code: string;
    courier_name: string;
    courier_id: number;
  };
}

export interface GeneratePDFResponse {
  status: number;
  label_url?: string;
  manifest_url?: string;
  invoice_url?: string;
  message?: string;
}

export interface TrackShipmentResponse {
  tracking_data: {
    ship_status: number;
    status: string;
    awb_code: string;
    courier_name: string;
    etd: string;
    tracking_events: Array<{
      scan_type: string;
      scan_date_time: string;
      location: string;
      status_code: string;
      status: string;
    }>;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ShiprocketService {
  private readonly baseUrl = environment.shiprocket.baseUrl;
  private readonly functionsBaseUrl = `https://${environment.firebaseFunctions.region}-${environment.firebaseFunctions.projectId}.cloudfunctions.net`;
  
  constructor(
    private http: HttpClient,
    private toastService: ToastService
  ) {}

  /**
   * Check serviceability for a pincode
   */
  checkServiceability(pincode: string, weight: number = 0.5, cod: 0 | 1 = 0): Observable<ServiceabilityResponse> {
    const params = new URLSearchParams({
      pickup_pincode: environment.shiprocket.warehouse.pincode || '',
      delivery_pincode: pincode,
      weight: weight.toString(),
      cod: cod.toString()
    });

    return this.http.get<{ success: boolean; serviceability: ServiceabilityResponse }>(
      `${this.functionsBaseUrl}/checkShiprocketServiceability?${params}`
    ).pipe(
      map(response => response.serviceability),
      catchError(error => {
        console.error('Serviceability check error:', error);
        this.toastService.error('Failed to check serviceability. Please verify pincode.');
        return throwError(() => new Error(error?.error?.message || 'Serviceability check failed'));
      })
    );
  }

  /**
   * Generate pickup request
   */
  generatePickup(shipmentId: number): Observable<any> {
    return this.http.post<any>(
      `${this.functionsBaseUrl}/generateShiprocketPickup`,
      { shipment_id: shipmentId }
    ).pipe(
      catchError(error => {
        console.error('Generate pickup error:', error);
        this.toastService.error('Failed to generate pickup request.');
        return throwError(() => new Error(error?.error?.message || 'Pickup generation failed'));
      })
    );
  }

  /**
   * Generate manifest
   */
  generateManifest(shipmentId: number): Observable<GeneratePDFResponse> {
    return this.http.post<GeneratePDFResponse>(
      `${this.functionsBaseUrl}/generateShiprocketManifest`,
      { shipment_id: shipmentId }
    ).pipe(
      catchError(error => {
        console.error('Generate manifest error:', error);
        this.toastService.error('Failed to generate manifest.');
        return throwError(() => new Error(error?.error?.message || 'Manifest generation failed'));
      })
    );
  }

  /**
   * Generate shipping label
   */
  generateLabel(shipmentId: number): Observable<GeneratePDFResponse> {
    const params = new URLSearchParams({
      shipment_id: shipmentId.toString()
    });

    return this.http.get<GeneratePDFResponse>(
      `${this.functionsBaseUrl}/generateShiprocketLabel?${params}`
    ).pipe(
      catchError(error => {
        console.error('Generate label error:', error);
        this.toastService.error('Failed to generate shipping label.');
        return throwError(() => new Error(error?.error?.message || 'Label generation failed'));
      })
    );
  }

  /**
   * Generate invoice
   */
  generateInvoice(shipmentId: number): Observable<GeneratePDFResponse> {
    const params = new URLSearchParams({
      shipment_id: shipmentId.toString()
    });

    return this.http.get<GeneratePDFResponse>(
      `${this.functionsBaseUrl}/generateShiprocketInvoice?${params}`
    ).pipe(
      catchError(error => {
        console.error('Generate invoice error:', error);
        this.toastService.error('Failed to generate invoice.');
        return throwError(() => new Error(error?.error?.message || 'Invoice generation failed'));
      })
    );
  }

  /**
   * Track shipment by AWB code
   */
  trackShipment(awbCode: string): Observable<TrackShipmentResponse> {
    const params = new URLSearchParams({
      awb_code: awbCode
    });

    return this.http.get<{ success: boolean; tracking_data: any }>(
      `${this.functionsBaseUrl}/trackShiprocketShipment?${params}`
    ).pipe(
      map(response => ({ tracking_data: response.tracking_data })),
      catchError(error => {
        console.error('Track shipment error:', error);
        this.toastService.error('Failed to track shipment.');
        return throwError(() => new Error(error?.error?.message || 'Tracking failed'));
      })
    );
  }
}

